/**
 * 관리자 유료회원 이탈방어 대시보드
 * ($j.Pages.Admin.ChurnGuard / body data-page-name="ChurnGuard")
 *
 * 어플발주(order_type=0) 한정 수주충족률 추이 + 이탈위험 회원 리스트.
 */
import Util from '../../Common/Util';

export default class ChurnGuard {
    constructor() {
        this.trendChart = null;
        this.breakdownChart = null;
        this.$riskTable = null;
        this.FAIL = {
            won:       { label: '정상',       color: '#198754' },
            lost:      { label: '선착순패배', color: '#dc3545' },
            starved:   { label: '굶주림',     color: '#fd7e14' },
            unmatched: { label: '자격부족',   color: '#6c757d' },
        };
    }

    initialize() {
        this._bindUi();
        this._loadAll();
    }

    _bindUi() {
        $('#cgRefresh').on('click', () => this._loadAll(true));
    }

    _loadAll(nocache = false) {
        this._loadSummary(nocache);
        this._loadRisk(nocache);
    }

    async _loadSummary(nocache = false) {
        try {
            const res = await $.ajax({
                url: '/adm/ajax/stat/churn-guard/summary', type: 'get',
                data: nocache ? { nocache: 1 } : {}
            });
            if (!res.ok) return;
            this._renderCards(res.summary);
            this._renderTrend(res.trend);
            this._renderFunnel(res.funnel);
            this._renderBreakdown(res.breakdown);
            if (res.cached_at) $('#cgCachedAt').text(`집계시각 ${res.cached_at}`);
        } catch (e) {
            console.error('이탈방어 요약 로드 실패', e);
        }
    }

    _renderFunnel(f) {
        if (!f) return;
        $('#cgFunnelDays').text(`(최근 ${f.days}일 · 어플발주 ${this._comma(f.app_orders)}건)`);
        $('#cgFunCand').text(this._comma(Math.round(f.avg_candidates)));
        $('#cgFunAlert').text(this._comma(Math.round(f.avg_alerted)));
        const reachPct = f.avg_candidates > 0 ? Math.round(f.avg_alerted / f.avg_candidates * 100) : 0;
        $('#cgFunAlertPct').text(`후보의 ${reachPct}%만 도달`);
        $('#cgFunWin').text(f.win_rate + '%');
        $('#cgFunWinSub').text(`수주 ${this._comma(f.won_orders)} / ${this._comma(f.app_orders)}`);
    }

    _renderBreakdown(rows) {
        if (!rows) return;
        const ctx = document.getElementById('cgBreakdownChart').getContext('2d');
        if (this.breakdownChart) this.breakdownChart.destroy();

        const order = ['won', 'lost', 'starved', 'unmatched'];
        const map = {};
        rows.forEach(r => { map[r.fail_type] = Number(r.members); });
        const labels = order.map(k => this.FAIL[k].label);
        const data = order.map(k => map[k] || 0);
        const colors = order.map(k => this.FAIL[k].color);

        this.breakdownChart = new Chart(ctx, {
            type: 'doughnut',
            data: { labels, datasets: [{ data, backgroundColor: colors }] },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'right' },
                    tooltip: {
                        callbacks: {
                            label: (c) => `${c.label}: ${this._comma(c.raw)}명`
                        }
                    }
                }
            }
        });
    }

    _renderCards(s) {
        const rate = Number(s.rate);
        const cls = rate < 40 ? 'cg-rate-low' : (rate < 55 ? 'cg-rate-mid' : 'cg-rate-ok');
        const $rate = $('#cgRate');
        $rate.text(rate + '%').removeClass('cg-rate-low cg-rate-mid cg-rate-ok').addClass(cls);
        $('#cgCurMonth').text('(' + this._fmtMonth(s.month_code) + ' 진행중)');
        $('#cgRateSub').text(`수주 ${this._comma(s.won)} / 유료 ${this._comma(s.paid_tier)}`);
        $('#cgPaid').text(this._comma(s.paid_tier));
        $('#cgWon').text(this._comma(s.won));
        $('#cgRisk').text(this._comma(s.risk_cnt));
    }

    _renderTrend(trend) {
        const ctx = document.getElementById('cgTrendChart').getContext('2d');
        if (this.trendChart) this.trendChart.destroy();

        const labels = trend.map(r => this._fmtMonth(r.month_code));
        const rates = trend.map(r => Number(r.rate));
        // 진행중(부분월) 포인트는 색을 다르게
        const pointColors = trend.map(r => Number(r.is_partial) ? '#fd7e14' : '#0d6efd');

        this.trendChart = new Chart(ctx, {
            type: 'line',
            data: {
                labels,
                datasets: [{
                    label: '수주충족률 (%)',
                    data: rates,
                    borderColor: '#0d6efd',
                    backgroundColor: 'rgba(13,110,253,.08)',
                    pointBackgroundColor: pointColors,
                    pointRadius: 5,
                    fill: true,
                    tension: .25,
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: { beginAtZero: true, max: 100, ticks: { callback: v => v + '%' } }
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            afterBody: (items) => {
                                const i = items[0].dataIndex;
                                const r = trend[i];
                                const partial = Number(r.is_partial) ? ' (진행중)' : '';
                                return `유료 ${this._comma(r.paid_tier)} / 수주 ${this._comma(r.won)}${partial}`;
                            }
                        }
                    }
                }
            }
        });
    }

    async _loadRisk(nocache = false) {
        const res = await $.ajax({
            url: '/adm/ajax/stat/churn-guard/risk', type: 'get',
            data: nocache ? { nocache: 1 } : {}
        });
        if (!res.ok) return;
        $('#cgRiskCount').text(`총 ${this._comma(res.data.length)}명`);
        this._renderRiskTable(res.data);
    }

    _renderRiskTable(data) {
        if (this.$riskTable) { this.$riskTable.destroy(); $('#cgRiskTable').empty(); }

        this.$riskTable = new DataTable('#cgRiskTable', {
            data,
            columns: [
                // 회원 표기는 관리자 공용 규약 — `이름#번호` + 누르면 회원 팝오버 (.ai/frontend.md)
                { data: null, title: '회원',
                    render: (d, t, r) => $j.F.Admin.Member.Pop.cell(r.mb_no, r.mem_name) },
                { data: 'mem_phone', title: '연락처', render: (v) => Util.escapeHtml(v) },
                { data: 'mem_car_type', title: '장비', render: (v) => Util.escapeHtml(v) },
                {
                    data: 'pay_source', title: '회비',
                    render: (v) => `<span class="cg-badge cg-badge-${v}">${this._sourceLabel(v)}</span>`
                },
                {
                    data: 'fail_type', title: '실패유형',
                    render: (v) => {
                        const f = this.FAIL[v] || { label: v, color: '#999' };
                        return `<span class="cg-badge" style="background:${f.color}">${f.label}</span>`;
                    }
                },
                { data: 'matched_5d', title: '매칭(5일)', render: (v) => this._comma(Number(v)) },
                { data: 'alerted_5d', title: '알림(5일)', render: (v) => this._comma(Number(v)) },
                {
                    data: 'last_won', title: '마지막 수주',
                    render: (v) => v ? `${v} <small class="text-muted">(${this._monthsSince(v)})</small>` : '<span class="text-danger">수주이력 없음</span>'
                },
            ],
            // 회원번호+이름을 한 칸으로 합치면서 뒤가 한 칸씩 당겨졌다.
            // 전에는 이 번호가 '매칭(5일)' 을 가리키고 있었다(주석과 어긋나 있었다) — 지금은 맞다.
            order: [[6, 'desc']], // 알림(5일) 많은 순
            pageLength: 50,
            lengthChange: true,
            searching: true,
            autoWidth: false,
        });
    }

    _sourceLabel(v) {
        return { cms: 'CMS', cash: '캐시', prepay: '선결제', nofee: '감면', exempt: '면제' }[v] || v;
    }

    _monthsSince(dateStr) {
        const d = new Date(dateStr);
        if (isNaN(d)) return '';
        const now = new Date();
        const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
        return months <= 0 ? '이번달' : `${months}개월 전`;
    }

    _fmtMonth(mc) {
        const s = String(mc);
        return s.length === 6 ? `${s.slice(0, 4)}.${s.slice(4)}` : s;
    }

    _comma(n) {
        return Number(n || 0).toLocaleString();
    }

    destroy() {
        if (this.trendChart) { this.trendChart.destroy(); this.trendChart = null; }
        if (this.$riskTable) { this.$riskTable.destroy(); this.$riskTable = null; }
    }
}
